import { useQuery } from '@tanstack/react-query'
import { useAppSelector } from '@/hooks'
import { selectTenant } from '@/features/auth/authSlice'
import { fetchSetupRun } from '@/api/modules/integrations'

/** React Query key namespace for a measurements setup run (FE-1). */
export const SETUP_RUN_QUERY_KEY = 'measurements-setup-run'

/** Poll interval, ms, while a setup run has an item still pending or running. */
export const SETUP_RUN_POLL_INTERVAL_MS = 2000

/**
 * Whether every item in a run's per-item results has settled into
 * `done`/`failed`/`blocked`. An empty/absent list is NOT settled - there is
 * nothing to conclude from a run whose results haven't been read yet.
 *
 * `blocked` (R-2, campus-map fix round): an item the backend refused to
 * materialize because it references an unmapped Rock campus (`error_code:
 * 'campus_map_incomplete'`, `unmapped_campuses` alongside it - see
 * `useSetupFlow.js`'s settle effect, which opens `CampusMatchDialog` for it
 * and re-posts the same item once the map is applied). It counts as settled
 * for the same reason `done`/`failed` do: nothing pending/running is left
 * to poll for.
 *
 * @param {Array<import('@/types/models/measurements').SetupItemStatus>} items
 * @returns {boolean}
 */
export function isRunSettled(items) {
  if (!Array.isArray(items) || items.length === 0) return false
  return items.every(
    (item) => item.status === 'done' || item.status === 'failed' || item.status === 'blocked',
  )
}

/**
 * Whether a setup run's `items` still has any item mid-flight.
 *
 * @param {import('@/types/models/measurements').SetupRun|undefined} data
 * @returns {boolean}
 */
export function hasUnsettledItem(data) {
  const items = data?.items
  if (data?.status === 'pending' || data?.status === 'running') return true
  if (!Array.isArray(items)) return false
  return items.some((item) => item.status === 'pending' || item.status === 'running')
}

/**
 * Poll a measurements setup run's status (BE-4's `run_measurement_setup_task`,
 * started by `startMeasurementsSetup`) until every item has settled into
 * `done` or `failed`.
 *
 * Polls every SETUP_RUN_POLL_INTERVAL_MS while `hasUnsettledItem` is true and
 * stops (react-query's `refetchInterval` returns `false`) once the run is
 * fully settled, so a finished setup screen left open doesn't keep hitting
 * the backend. Tenant-keyed and gated the same way as `useMeasurementsBoard`,
 * plus requiring a `runId`.
 *
 * A failed poll is deliberately NOT treated as a failed run. React Query keeps
 * `state.data` (the last good payload) when a later background refetch fails,
 * so once any poll has succeeded the polling continues off that last good
 * payload until the run itself settles - a dropped connection must not strand
 * a still-running job. Only a query that has never once succeeded stops on
 * error (nothing to keep tracking, and the caller has `isError` to surface).
 * Callers read the distinction the same way: `data` is the last known truth,
 * `isError` only means "the most recent status check didn't get through".
 *
 * @param {string|undefined} connectionId
 * @param {string|undefined} runId
 * @returns {import('@tanstack/react-query').UseQueryResult<import('@/types/models/measurements').SetupRun>}
 */
export function useSetupRun(connectionId, runId) {
  const tenantId = useAppSelector(selectTenant)?.id

  return useQuery({
    queryKey: [SETUP_RUN_QUERY_KEY, tenantId, connectionId, runId],
    queryFn: () => fetchSetupRun(connectionId, runId),
    enabled: !!tenantId && !!connectionId && !!runId,
    refetchInterval: (query) => {
      if (query.state.data) {
        // Keep polling off the last good payload, error or not, until settled.
        return hasUnsettledItem(query.state.data) ? SETUP_RUN_POLL_INTERVAL_MS : false
      }
      // Never a good payload: give up once react-query has (after its own
      // configured retries), rather than hammering an endpoint that won't answer.
      return query.state.status === 'error' ? false : SETUP_RUN_POLL_INTERVAL_MS
    },
  })
}
